import type { ApprovalItem, RegistryState, SetupValue } from '../../data/types';
import { accountContactFor, applyPatch, buildPrefill } from '../../domain/setup/prefill';
import {
  buildClass,
  buildFund,
  buildOffer,
  canActivate,
  canPublish,
} from '../../domain/setup/setupRules';
import { ACTIVATION_SECTIONS, OFFER_FORM_SECTIONS } from '../../domain/setup/sections';
import { DEMO_TODAY, nowStamp } from '../../lib/format';
import { newId } from '../../lib/random';
import { actorName, addApproval, withAudit } from '../helpers';

const paidPct = (values: Record<string, SetupValue>, issuePrice: number) => {
  const pct =
    Math.round(((Number(values['offer.payableNow']) || issuePrice) / issuePrice) * 10_000) / 100;
  return pct < 100 ? pct : undefined;
};

const uniqueId = (base: string, taken: Set<string>) => {
  let id = base;
  for (let suffix = 2; taken.has(id); suffix += 1) id = `${base}-${suffix}`;
  return id;
};

export function startSetup(
  state: RegistryState,
  action: { opportunityId: string; setupId: string },
): RegistryState {
  const opportunity = state.opportunities.find((item) => item.id === action.opportunityId);
  const user = state.users.find((item) => item.id === state.currentUserId);
  if (!opportunity || !user) return state;
  const { values, sources } = buildPrefill(
    opportunity,
    user,
    state.houseDefaults[state.currentTenantId],
    [
      ...state.funds.map((fund) => fund.code),
      ...state.fundSetups.map((setup) => String(setup.values['fund.code'] ?? '')),
    ],
  );
  const stamp = nowStamp();
  const next: RegistryState = {
    ...state,
    fundSetups: [
      {
        id: action.setupId,
        tenantId: state.currentTenantId,
        opportunityId: opportunity.id,
        status: 'DRAFT',
        values,
        prefill: { ...values },
        sources,
        completed: [],
        createdBy: user.name,
        createdAt: stamp,
        updatedAt: stamp,
      },
      ...state.fundSetups,
    ],
  };
  return withAudit(next, 'Deal setup started', opportunity.name, 'Pre-filled from the opportunity');
}

export function updateSetup(
  state: RegistryState,
  action: { id: string; patch: Record<string, SetupValue> },
): RegistryState {
  return {
    ...state,
    fundSetups: state.fundSetups.map((setup) =>
      setup.id === action.id
        ? {
            ...setup,
            ...applyPatch(setup.values, setup.prefill, action.patch),
            updatedAt: nowStamp(),
          }
        : setup,
    ),
  };
}

export function completeSetupSection(
  state: RegistryState,
  action: { id: string; sectionId: string },
): RegistryState {
  return {
    ...state,
    fundSetups: state.fundSetups.map((setup) =>
      setup.id === action.id && !setup.completed.includes(action.sectionId)
        ? { ...setup, completed: [...setup.completed, action.sectionId] }
        : setup,
    ),
  };
}

export function activateSetup(state: RegistryState, action: { id: string }): RegistryState {
  const setup = state.fundSetups.find((item) => item.id === action.id);
  if (!setup || !canActivate(setup)) return state;
  const code = String(setup.values['fund.code']).toLowerCase();
  const fundId = uniqueId(`f-${code}`, new Set(state.funds.map((fund) => fund.id)));
  const classId = uniqueId(
    `c-${code}-${String(setup.values['class.code']).toLowerCase()}`,
    new Set(state.classes.map((unitClass) => unitClass.id)),
  );
  const fund = buildFund(setup, fundId, DEMO_TODAY);
  const next: RegistryState = {
    ...state,
    funds: [...state.funds, fund],
    classes: [...state.classes, buildClass(setup, fundId, classId)],
    fundSetups: state.fundSetups.map((item) =>
      item.id === setup.id
        ? {
            ...item,
            status: 'ACTIVE',
            fundId,
            classId,
            activatedAt: nowStamp(),
            completed: Array.from(new Set([...item.completed, ...ACTIVATION_SECTIONS, 'activate'])),
          }
        : item,
    ),
  };
  return withAudit(next, 'Register activated', fund.name, `Trust and ${classId} created`);
}

export function publishSetup(state: RegistryState, action: { id: string }): RegistryState {
  const setup = state.fundSetups.find((item) => item.id === action.id);
  if (!setup || !canPublish(setup)) return state;
  const offerId = newId('of');
  const offer = buildOffer(setup, offerId, nowStamp(), actorName(state));
  const next: RegistryState = {
    ...state,
    offers: [offer, ...state.offers],
    funds: state.funds.map((fund) =>
      fund.id === setup.fundId ? { ...fund, status: 'RAISING' } : fund,
    ),
    classes: state.classes.map((unitClass) =>
      unitClass.id === setup.classId
        ? {
            ...unitClass,
            open: true,
            minInvestment: offer.minInvestment,
            paidOnApplicationPct: paidPct(setup.values, unitClass.issuePrice),
          }
        : unitClass,
    ),
    fundSetups: state.fundSetups.map((item) =>
      item.id === setup.id
        ? {
            ...item,
            status: 'PUBLISHED',
            offerId,
            publishedAt: offer.publishedAt,
            completed: Array.from(new Set([...item.completed, ...OFFER_FORM_SECTIONS, 'review'])),
          }
        : item,
    ),
  };
  return withAudit(next, 'Offer published', offer.title, offer.url);
}

export function discardSetup(state: RegistryState, action: { id: string }): RegistryState {
  const setup = state.fundSetups.find((item) => item.id === action.id);
  if (!setup || setup.status !== 'DRAFT') return state;
  return {
    ...state,
    fundSetups: state.fundSetups.filter((item) => item.id !== action.id),
  };
}

export function chooseSetupContact(
  state: RegistryState,
  action: { id: string; userId: string },
): RegistryState {
  const user = state.users.find(
    (item) => item.id === action.userId && item.tenantId === state.currentTenantId,
  );
  if (!user) return state;
  const contact = accountContactFor(user);
  return {
    ...state,
    fundSetups: state.fundSetups.map((setup) => {
      if (setup.id !== action.id) return setup;
      const next = applyPatch(setup.values, setup.prefill, contact);
      return {
        ...setup,
        values: next.values,
        prefill: { ...next.prefill, ...contact },
        updatedAt: nowStamp(),
      };
    }),
  };
}

export function updateHouseDefaults(
  state: RegistryState,
  action: { patch: Record<string, SetupValue> },
): RegistryState {
  const tenantId = state.currentTenantId;
  const next: RegistryState = {
    ...state,
    houseDefaults: {
      ...state.houseDefaults,
      [tenantId]: { ...(state.houseDefaults[tenantId] ?? {}), ...action.patch },
    },
  };
  return withAudit(
    next,
    'Fund defaults updated',
    'House defaults',
    `${Object.keys(action.patch).length} settings changed; applies to new deals`,
  );
}

export function requestPublish(state: RegistryState, action: { id: string }): RegistryState {
  const setup = state.fundSetups.find((item) => item.id === action.id);
  if (!setup || !canPublish(setup) || setup.publishRequestedAt) return state;
  const title = String(setup.values['offer.title']);
  let next: RegistryState = {
    ...state,
    fundSetups: state.fundSetups.map((item) =>
      item.id === setup.id
        ? { ...item, publishRequestedBy: actorName(state), publishRequestedAt: nowStamp() }
        : item,
    ),
  };
  next = addApproval(next, {
    kind: 'OFFER_PUBLISH',
    refId: setup.id,
    fundId: setup.fundId,
    title: `Publish offer: ${title}`,
    summary: `Opens ${String(setup.values['class.name'])} to applications and shows it to existing investors.`,
    amount: Number(setup.values['fund.targetSize']) || undefined,
  });
  return withAudit(next, 'Offer sent for approval to publish', title, 'Second person to publish');
}

export function applyOfferPublish(
  state: RegistryState,
  item: ApprovalItem,
  approve: boolean,
): RegistryState {
  if (approve) return publishSetup(state, { id: item.refId });
  return {
    ...state,
    fundSetups: state.fundSetups.map((setup) =>
      setup.id === item.refId
        ? { ...setup, publishRequestedBy: undefined, publishRequestedAt: undefined }
        : setup,
    ),
  };
}
